import { FC, useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { BrandAsset, BrandAssetKind, LogoType } from "../../../shared/models/brand.model";
import { BrandAssetEntry, newAssetEntry } from "../hooks/brandAssetEntries";
import { removedRequiredFields } from "../hooks/brandRequiredFields";
import BrandAssetPreview, { prefersDarkCanvas } from "./BrandAssetPreview";

/*
    An asset section of the Branding tab behind `brand-identity-v2`: Logos (MPN-2028), and
    Favicon, Icons, Graphics/Patterns and the Image/Video Library (MPN-2029). Each is the
    brand's assets of one kind.

    Each asset is a tile plus its details. Details, removals and added files are kept on
    the page until the section is saved. Replace and "Set as default" act at once, the way
    the logo tile grid always has; the default logo is the Primary logo, so setting one
    makes it Primary on the server.

    Primary and Square Format are required (MPN-2174): a missing one shows a hint, and a
    save that would leave none once one is saved, by removing it or changing its type with
    no replacement, is blocked. Every other save goes through. Each is one per brand, and the server's 409 naming the asset
    that already holds it is shown on the asset that asked. The favicon is one per brand
    the same way.

    Logos, icons and the favicon are images; the library also takes MP4, MOV and WebM,
    and whether an asset is an image or a video is read off its file. The server refuses
    a video where only an image will do, and that message is shown the same way.
*/

interface Props {
  kind: BrandAssetKind;
  /** Every asset as edited; this section shows those of `kind`. */
  assets: BrandAssetEntry[];
  /** The brand's assets as last saved, of every kind. */
  savedAssets: BrandAsset[];
  setAssets: (update: (prev: BrandAssetEntry[]) => BrandAssetEntry[]) => void;
  onSave: () => Promise<void>;
  onReplace: (assetId: number, file: File) => Promise<void>;
  onSetDefault: (assetId: number) => Promise<void>;
  isLoading: boolean;
}

export const LOGO_TYPES: { value: LogoType; label: string }[] = [
  { value: "PRIMARY", label: "Primary" },
  { value: "LIGHT_BACKGROUND", label: "Light Background" },
  { value: "DARK_BACKGROUND", label: "Dark Background" },
  { value: "ICON_ONLY", label: "Icon Only" },
  { value: "HORIZONTAL", label: "Horizontal" },
  { value: "STACKED_VERTICAL", label: "Stacked/Vertical" },
  { value: "SQUARE_FORMAT", label: "Square Format" },
];

const REQUIRED_LOGO_TYPES: LogoType[] = ["PRIMARY", "SQUARE_FORMAT"];

interface SectionConfig {
  title: string;
  hint: string;
  /** One asset, e.g. "Logo", and several, e.g. "logos". */
  noun: string;
  plural: string;
  accept: string;
  /** Has a Description field. */
  described?: boolean;
  /** One per brand: no Add once there is one. */
  single?: boolean;
}

const IMAGES = "image/jpeg,image/png,image/webp,image/svg+xml,.jpg,.jpeg,.png,.webp,.svg";
const IMAGES_AND_VIDEO = `${IMAGES},video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm`;

const SECTIONS: Record<BrandAssetKind, SectionConfig> = {
  LOGO: {
    title: "Logos",
    hint: "Every variant on file for this brand. The default is the Primary logo, which Create applies when rebranding ads.",
    noun: "Logo",
    plural: "logos",
    accept: IMAGES,
  },
  FAVICON: {
    title: "Favicon",
    hint: "The small icon browsers show for the brand's site. One per brand.",
    noun: "Favicon",
    plural: "favicons",
    accept: IMAGES,
    single: true,
  },
  ICON: {
    title: "Icons",
    hint: "The brand's icon set.",
    noun: "Icon",
    plural: "icons",
    accept: IMAGES,
  },
  GRAPHIC_PATTERN: {
    title: "Graphics/Patterns",
    hint: "Graphic elements and patterns that belong to the brand.",
    noun: "Graphic/Pattern",
    plural: "graphics/patterns",
    accept: IMAGES,
    described: true,
  },
  LIBRARY: {
    title: "Image/Video Library",
    hint: "Approved photos and video for this brand. Video: MP4, MOV or WebM.",
    noun: "Image/Video",
    plural: "images/videos",
    accept: IMAGES_AND_VIDEO,
    described: true,
  },
};

const logoTypeLabel = (value: LogoType) => LOGO_TYPES.find((t) => t.value === value)?.label ?? value;

/** A picked file shown before it's uploaded. */
const PickedFilePreview: FC<{ file: File; label: string; mediaType: string }> = ({ file, label, mediaType }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url ? (
    <BrandAssetPreview url={url} label={label} mediaType={mediaType} startDark={false} downloadable={false} />
  ) : (
    <div className="brand-tile-canvas" />
  );
};

const BrandAssetSection: FC<Props> = ({ kind, assets, savedAssets, setAssets, onSave, onReplace, onSetDefault, isLoading }) => {
  const config = SECTIONS[kind];
  const nextKey = useRef(0);
  const entries = assets.filter((a) => a.kind === kind);
  const isLogo = kind === "LOGO";
  const missing = isLogo ? REQUIRED_LOGO_TYPES.filter((t) => !entries.some((e) => e.logo_type === t)) : [];
  const removed = isLogo
    ? removedRequiredFields(
        { logoTypes: savedAssets.filter((a) => a.kind === kind).map((a) => a.logo_type) },
        { logoTypes: entries.map((e) => e.logo_type) },
      )
    : [];
  const { plural } = config;

  const setField = (key: string, field: keyof BrandAssetEntry, value: string | null) => {
    setAssets((prev) => prev.map((a) => (a.key === key ? { ...a, [field]: value } : a)));
  };

  const addFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    nextKey.current += 1;
    setAssets((prev) => [...prev, newAssetEntry(`new-${kind}-${nextKey.current}`, kind, file)]);
  };

  const replaceFile = (e: React.ChangeEvent<HTMLInputElement>, assetId: number) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) void onReplace(assetId, file);
  };

  const remove = (key: string) => setAssets((prev) => prev.filter((a) => a.key !== key));

  return (
    <div className="brand-section">
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h6 className="brand-section-title">{config.title}</h6>
          <p className="brand-section-hint">{config.hint}</p>
        </div>
        <span className="brand-section-count">
          {entries.length} {entries.length === 1 ? config.noun.toLowerCase() : plural}
        </span>
      </div>

      {missing.map((logoType) => (
        <div key={logoType} className="small text-muted mb-2">
          <i className="bi bi-info-circle me-1"></i>A {logoTypeLabel(logoType)} logo is required.
        </div>
      ))}

      {entries.length === 0 && (
        <div className="text-center text-muted py-4">
          <i className="bi bi-image fs-3 d-block mb-2"></i>
          <div className="small">No {plural} on file yet</div>
        </div>
      )}

      {entries.map((entry, i) => {
        const label = entry.name || entry.file?.name || `${config.noun} ${i + 1}`;
        const saved = entry.id !== null;
        const startDark = entry.logo_type === "DARK_BACKGROUND" || prefersDarkCanvas(entry.tag, entry.name);
        return (
          <div key={entry.key} className="brand-asset-row mt-3" role="group" aria-label={label}>
            <Row>
              <Col md={3}>
                <div className="brand-tile">
                  {entry.file ? (
                    <PickedFilePreview file={entry.file} label={label} mediaType={entry.media_type} />
                  ) : (
                    <BrandAssetPreview url={entry.url ?? ""} label={label} mediaType={entry.media_type} startDark={startDark} />
                  )}
                  {saved && (
                    <div className="brand-tile-actions">
                      <input
                        type="file"
                        id={`asset-replace-${entry.key}`}
                        accept={config.accept}
                        className="d-none"
                        onChange={(e) => replaceFile(e, entry.id as number)}
                      />
                      <label
                        htmlFor={`asset-replace-${entry.key}`}
                        className={`brand-tile-select ${isLoading ? "is-busy" : ""}`}
                        title={`Replace the ${label} file`}
                      >
                        Replace
                      </label>
                      {isLogo && entry.logo_type !== "PRIMARY" && (
                        <button
                          type="button"
                          className="brand-tile-select"
                          disabled={isLoading}
                          title="Makes it the Primary logo"
                          aria-label={`Set ${label} as the default logo`}
                          onClick={() => onSetDefault(entry.id as number)}
                        >
                          Set as default
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </Col>
              <Col md={9}>
                <Row>
                  <Col md={isLogo ? 4 : 11}>
                    <Form.Group className="mb-2" controlId={`asset-${entry.key}-name`}>
                      <Form.Label className="small fw-semibold mb-1">Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={entry.name ?? ""}
                        placeholder={entry.file?.name}
                        onChange={(e) => setField(entry.key, "name", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  {isLogo && (
                    <>
                      <Col md={4}>
                        <Form.Group className="mb-2" controlId={`asset-${entry.key}-logo-type`}>
                          <Form.Label className="small fw-semibold mb-1">Logo Type</Form.Label>
                          <Form.Select
                            value={entry.logo_type ?? ""}
                            onChange={(e) => setField(entry.key, "logo_type", e.target.value || null)}
                          >
                            <option value="">Not set</option>
                            {LOGO_TYPES.map(({ value, label: typeLabel }) => (
                              <option key={value} value={value}>
                                {typeLabel}
                                {REQUIRED_LOGO_TYPES.includes(value) ? " *" : ""}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-2" controlId={`asset-${entry.key}-minimum-size`}>
                          <Form.Label className="small fw-semibold mb-1">Minimum Logo Size</Form.Label>
                          <Form.Control
                            type="text"
                            value={entry.minimum_size ?? ""}
                            placeholder="e.g. 120px wide"
                            onChange={(e) => setField(entry.key, "minimum_size", e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </>
                  )}
                  <Col md={1} className="d-flex align-items-end justify-content-end mb-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      aria-label={`Remove ${label}`}
                      onClick={() => remove(entry.key)}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </Col>
                  {config.described && (
                    <Col md={12}>
                      <Form.Group className="mb-2" controlId={`asset-${entry.key}-description`}>
                        <Form.Label className="small fw-semibold mb-1">Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={entry.description ?? ""}
                          onChange={(e) => setField(entry.key, "description", e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={12}>
                    <Form.Group className="mb-2" controlId={`asset-${entry.key}-usage`}>
                      <Form.Label className="small fw-semibold mb-1">Usage Guidelines</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={entry.usage_guidelines ?? ""}
                        onChange={(e) => setField(entry.key, "usage_guidelines", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {entry.error && (
                  <div className="small text-danger" role="alert">
                    <i className="bi bi-exclamation-circle me-1"></i>
                    {entry.error}
                  </div>
                )}
              </Col>
            </Row>
          </div>
        );
      })}

      {removed.map((field) => (
        <div key={field} className="small text-danger mt-3" role="alert">
          <i className="bi bi-exclamation-circle me-1"></i>
          Saving would leave no {field}. Keep it, or give another logo that type.
        </div>
      ))}

      <div className="d-flex justify-content-between mt-3">
        <div>
          {!(config.single && entries.length > 0) && (
            <>
              <input
                type="file"
                id={`asset-add-${kind}`}
                accept={config.accept}
                className="d-none"
                onChange={addFile}
              />
              <label
                htmlFor={`asset-add-${kind}`}
                className={`btn btn-sm btn-outline-primary mb-0 ${isLoading ? "disabled" : ""}`}
              >
                <i className="bi bi-plus-lg me-1"></i>
                Add {config.noun}
              </label>
            </>
          )}
        </div>
        <Button variant="primary" size="sm" onClick={() => onSave()} disabled={isLoading || removed.length > 0}>
          Save {config.title}
        </Button>
      </div>
    </div>
  );
};

export default BrandAssetSection;
